import { useCallback, useContext, useMemo } from 'react'
import * as React from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import useModalElementContext from 'hooks/Modal/useModalElementContext'
import Heading from 'LuxKit/Typography/Heading'
import Group from 'components/utils/Group'
import BodyText from 'LuxKit/Typography/BodyText'
import InclusionsSection from 'components/OfferPage/FlashHotel/InclusionsSection'
import type { ImageParams } from 'components/Common/ResponsiveImage/ResponsiveImage'
import ResponsiveImage from 'components/Common/ResponsiveImage/ResponsiveImage'
import Divider from 'LuxKit/Divider'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import LePackageUpgradePrice from './LePackageUpgradePrice'
import { useAppSelector } from 'hooks/reduxHooks'
import LuxPlusInclusions from 'luxPlus/components/LuxPlusInclusions'
import { formatCurrency } from 'lib/format/formatCurrencyIntl'
import GeoContext from 'contexts/geoContext'
import CurrencyContext from 'contexts/currencyContext'
import { getCharterBundleRoute } from 'selectors/offerPage/initialOfferPageStateSelectors'
import { useOfferPageFlightPrice } from 'hooks/useFlightPrice'
import OfferPageStateContext from 'contexts/OfferPage/offerPageStateContext'
import { MALDIVES_CHARTER_BUNDLE_CAMPAIGN_TAG } from 'constants/charterFlight'
import { isConfiguredFeederOriginAirportCode } from 'lib/charterFlights/charterBundleOfferPageUtils'
import { addFlightPrice } from 'lib/offer/pricing'
import ModalHeader from 'LuxKit/Modal/ModalHeader'
import ModalBody from 'LuxKit/Modal/ModalBody'
import ModalContent from 'LuxKit/Modal/ModalContent'
import ModalFooter from 'LuxKit/Modal/ModalFooter'
import ImageGallery from 'LuxKit/Carousel/ImageGallery'
import ModalBase from 'LuxKit/Modal/ModalBase'
import Breakpoint from 'components/Common/Breakpoint'
import PackageUpgradeSurchargeMarginComparisonText from './PackageUpgradeSurchargeMarginTexts/PackageUpgradeSurchargeMarginComparisonText'
import { connect } from 'react-redux'
import { generateLoyaltyPointsCalculatorHotelOptions } from 'loyalty/lib/pointsCalculation/calculatorOptionsGenerators'
import PriceRowLoyaltyPoints from 'LuxKit/PricePoints/Loyalty/PriceRowLoyaltyPoints'
import useLoyaltyPointsEarnCalculator from 'loyalty/hooks/useLoyaltyPointsEarnCalculator'
import getLoyaltyDisplayTier from 'loyalty/selectors/getLoyaltyDisplayTier'
import PriceRowLoyaltyPointsLoadingSkeleton from 'LuxKit/PricePoints/Loyalty/PriceRowLoyaltyPointsLoadingSkeleton'
import getIsLoyaltyEnabled from 'loyalty/selectors/getIsLoyaltyEnabled'
import getIsCurrentRegionUserLoyaltyRegion from 'loyalty/selectors/getIsCurrentRegionUserLoyaltyRegion'
import LoyaltyGenericInfoModal from 'loyalty/components/LoyaltyGenericInfoModal'
import ModalContext from 'contexts/ModalContext'
import { trackClientEvent } from 'analytics/analytics'
import OfferPageStateTrackingContext from 'contexts/OfferPage/offerPageStateTrackingContext'
import getShouldHideLoyaltyStatusCreditsAndNights from 'loyalty/selectors/getShouldHideLoyaltyStatusCreditsAndNights'
import { ChertoExperiments } from 'constants/experiments'
import useExperiment from 'hooks/Experiments/useExperiment'
import { useScreenSizeOnly, useScreenSizeUp } from 'hooks/useScreenSize'
import StayUpgradeModalVariant from './StayUpgradeModalVariant'
import type { PackageOptionUpgradeView } from './PackageUpgradeModal.types'
import PackageUpgradeRoomDetailsExpander from './PackageUpgradeRoomDetailsExpander'
import { isSameStayUpgradeRoomType } from './stayUpgradeModalUtils'
import usePackageUpgradeInclusions from './usePackageUpgradeInclusions'
import useLuxPlusPricingContext from 'hooks/useLuxPlusPricingContext'
import { getLuxPlusPayableUpgradePriceDifference } from 'components/OfferPage/offerPageUtils/propertyLoyaltyPackageUpgrade'

export type { PackageOptionUpgradeView } from './PackageUpgradeModal.types'

const MIN_UPGRADE_VALUE_SAVING = 0.2

type PackageUpgradePricing = PackageOptionUpgradeView & {
  basePrice: number
  baseMemberPrice: number
}

const imageProps: ImageParams = {
  mobileAspectRatio: '11:5',
  tabletAspectRatio: '22:15',
  tabletWidth: '200px',
  quality: 'good',
}

interface MappedProps {
  isSpoofed: boolean
  isStoreMode: boolean
  isLoyaltyEnabled: boolean
}

interface Props {
  offer: App.HotelOffer | App.VillaOffer
  upgradePackageView: App.OfferPackageOptionView
  basePackageView: App.OfferPackageOptionView
  checkIn?: string
  hasFlights?: boolean
}

function PackageUpgradeModal(props: Props & MappedProps) {
  const { offer, upgradePackageView, basePackageView, hasFlights, checkIn, isSpoofed, isStoreMode, isLoyaltyEnabled } = props
  const intl = useIntl()
  const isMobile = useScreenSizeOnly('mobile')
  const isTabletOrDesktop = useScreenSizeUp('tablet')
  const { isAnyVariant: isStayUpgradeModalMobileVariant } = useExperiment(
    ChertoExperiments.croMobileWebRoomUpgradeModal,
    { canActivate: isMobile },
  )
  const { isAnyVariant: isStayUpgradeModalDesktopVariant } = useExperiment(
    ChertoExperiments.croDesktopWebRoomUpgradeModal,
    { canActivate: isTabletOrDesktop },
  )
  const modalContext = useModalElementContext<App.OfferPackageOptionView>()
  const { trackEvent } = useContext(OfferPageStateTrackingContext)
  const { airport: flightOrigin } = useContext(OfferPageStateContext)
  const charterBundleRoute = useAppSelector(state => getCharterBundleRoute(state, offer))

  // Use the same flight price source as FlashHotelOptionPriceDetails.tsx
  const { totalFlightPrice: flightPrice, hasFeederFlightPriced } = useOfferPageFlightPrice()
  const shouldHideStatusCreditsAndNights = useAppSelector(getShouldHideLoyaltyStatusCreditsAndNights)

  const packageUpgrade = useMemo<PackageUpgradePricing>(() => {
    // In charter mode, use addFlightPrice like FlashHotelOptionPriceDetails.tsx
    if (charterBundleRoute && flightPrice) {
      const upgradePricingWithFlights = addFlightPrice(upgradePackageView.pricing, flightPrice)
      const basePricingWithFlights = addFlightPrice(basePackageView.pricing, flightPrice)
      const upgradeMemberPricingWithFlights = upgradePackageView.memberPricing ? addFlightPrice(upgradePackageView.memberPricing, flightPrice) : undefined
      const baseMemberPricingWithFlights = basePackageView.memberPricing ? addFlightPrice(basePackageView.memberPricing, flightPrice) : undefined

      return {
        offer,
        priceDifference: upgradePricingWithFlights.price - basePricingWithFlights.price,
        memberPriceDifference: upgradeMemberPricingWithFlights && baseMemberPricingWithFlights ? upgradeMemberPricingWithFlights.price - baseMemberPricingWithFlights.price : upgradePricingWithFlights.price - basePricingWithFlights.price,
        basePrice: basePricingWithFlights.price,
        baseMemberPrice: baseMemberPricingWithFlights?.price ?? basePricingWithFlights.price,
        price: upgradePricingWithFlights.price,
        memberPrice: upgradeMemberPricingWithFlights?.price ?? upgradePricingWithFlights.price,
        package: upgradePackageView.package,
        value: upgradePricingWithFlights.value,
        flightPrice,
        flightsAvailable: upgradePackageView.flightsAvailable,
      }
    }

    // Normal mode (non-charter)
    return {
      offer,
      priceDifference: upgradePackageView.hotelPrice - basePackageView.hotelPrice,
      memberPriceDifference: upgradePackageView.hotelMemberPrice - basePackageView.hotelMemberPrice,
      basePrice: basePackageView.hotelPrice,
      baseMemberPrice: basePackageView.hotelMemberPrice,
      price: upgradePackageView.hotelPrice,
      memberPrice: upgradePackageView.hotelMemberPrice,
      package: upgradePackageView.package,
      value: upgradePackageView.hotelValue,
      flightPrice: upgradePackageView.flightPrice,
      flightsAvailable: upgradePackageView.flightsAvailable,
    }
  }, [basePackageView, upgradePackageView, offer, charterBundleRoute, flightPrice])

  const onUpgradeClick = useCallback(() => {
    trackEvent('UPGRADE_MODAL_ACCEPTED')
    trackClientEvent({
      subject: 'upgrade_modal',
      action: 'select_upgrade',
      category: 'logging',
      type: 'interaction',
    })
    modalContext.resolve(upgradePackageView)
  }, [modalContext, trackEvent, upgradePackageView])

  const onNoThanksClick = useCallback(() => {
    trackEvent('UPGRADE_MODAL_REJECTED')
    trackClientEvent({
      subject: 'upgrade_modal',
      action: 'no_upgrade',
      category: 'logging',
      type: 'interaction',
    })
    modalContext.resolve(basePackageView)
  }, [basePackageView, modalContext, trackEvent])

  const name = packageUpgrade.package.name
  const roomType = packageUpgrade.package.roomType
  const firstRoomImageId = packageUpgrade.package.roomType?.images?.[0]?.id
  const propertyLocation = [
    offer.property?.geoData.administrativeAreaLevel2,
    offer.property?.geoData.administrativeAreaLevel1,
    offer.property?.geoData.country,
  ].filter(Boolean).join(', ')
  const roomImageAlt = useMemo(() => {
    const roomName = roomType?.name ?? name
    const propertyName = offer.property?.name

    if (propertyName && propertyLocation) {
      return intl.formatMessage(
        {
          id: 'z8KvfmhB0Rfl0KNxRUOf',
          defaultMessage: '{roomName} image 1 at {propertyName} in {propertyLocation}',
          description: 'Alt text for the lead room image shown in the package upgrade modal',
        },
        {
          roomName,
          propertyName,
          propertyLocation,
        },
      )
    }

    if (propertyName) {
      return intl.formatMessage(
        {
          id: 'JzWVUzQRzUGhvqVpF9VV',
          defaultMessage: '{roomName} image 1 at {propertyName}',
          description: 'Alt text for the lead room image shown in the package upgrade modal',
        },
        {
          roomName,
          propertyName,
        },
      )
    }

    return intl.formatMessage(
      {
        id: 'vqGPywbDdIfdWPMwo5d9',
        defaultMessage: '{roomName} image 1',
        description: 'Alt text for the lead room image shown in the package upgrade modal',
      },
      {
        roomName,
      },
    )
  }, [intl, name, offer.property?.name, propertyLocation, roomType?.name])

  const isCharterBundle = offer.campaignTags?.includes(MALDIVES_CHARTER_BUNDLE_CAMPAIGN_TAG)
  const isFeederIncluded = isCharterBundle &&
    isConfiguredFeederOriginAirportCode(charterBundleRoute ?? 'melbourne-maldives', flightOrigin?.code) &&
    hasFeederFlightPriced
  const { inclusions, bonusInclusions, luxPlusInclusions } = usePackageUpgradeInclusions(packageUpgrade.package, {
    flightOrigin,
    hasFeederFlightIncluded: isFeederIncluded,
  })
  const showLuxPlusInclusions = !!luxPlusInclusions.length

  const { currentRegionCode } = useContext(GeoContext)
  const currency = useContext(CurrencyContext)
  const luxPlusPricingContext = useLuxPlusPricingContext()

  const basePackagePointsEarnCalculationRequests = useMemo<Array<App.LoyaltyPointsEarnCalculationRequest | undefined>>(() => [
    generateLoyaltyPointsCalculatorHotelOptions(offer, {
      packageView: basePackageView,
      currentRegionCode,
      nights: basePackageView?.package?.duration,
    }),
  ], [offer, basePackageView, currentRegionCode])

  const upgradePackagePointsEarnCalculationRequests = useMemo<Array<App.LoyaltyPointsEarnCalculationRequest | undefined>>(() => [
    generateLoyaltyPointsCalculatorHotelOptions(offer, {
      packageView: upgradePackageView,
      currentRegionCode,
      nights: upgradePackageView?.package?.duration,
    }),
  ], [offer, upgradePackageView, currentRegionCode])

  const { totalPointsCalculation: basePackagePointsEarnCalculation } = useLoyaltyPointsEarnCalculator(basePackagePointsEarnCalculationRequests)
  const { totalPointsCalculation: upgradePackagePointsEarnCalculation } = useLoyaltyPointsEarnCalculator(upgradePackagePointsEarnCalculationRequests)
  const isCurrentRegionUserLoyaltyRegion = useAppSelector(getIsCurrentRegionUserLoyaltyRegion)

  const anyPointsEarnFetching = basePackagePointsEarnCalculation.fetching || upgradePackagePointsEarnCalculation.fetching
  const anyPointsEarnError = basePackagePointsEarnCalculation.error || upgradePackagePointsEarnCalculation.error

  const basePackagePoints = basePackagePointsEarnCalculation.data?.points ?? 0
  const basePackageStatusCredits = basePackagePointsEarnCalculation.data?.statusCredits ?? 0

  const upgradePackagePoints = upgradePackagePointsEarnCalculation.data?.points ?? 0
  const upgradePackageStatusCredits = upgradePackagePointsEarnCalculation.data?.statusCredits ?? 0

  const upgradeAdditionalPointsEarn = upgradePackagePoints - basePackagePoints
  const upgradeAdditionalStatusCreditsEarn = upgradePackageStatusCredits - basePackageStatusCredits

  const displayTier = useAppSelector(getLoyaltyDisplayTier)

  const isSameRoomType = useMemo(() => (
    isSameStayUpgradeRoomType(basePackageView.package, upgradePackageView.package)
  ), [basePackageView.package, upgradePackageView.package])

  const payableUpgradePrice = useMemo(() => getLuxPlusPayableUpgradePriceDifference(
    packageUpgrade.basePrice,
    packageUpgrade.baseMemberPrice,
    packageUpgrade.price,
    packageUpgrade.memberPrice,
    luxPlusPricingContext,
  ), [luxPlusPricingContext, packageUpgrade.baseMemberPrice, packageUpgrade.basePrice, packageUpgrade.memberPrice, packageUpgrade.price])

  const upgradePriceFormat = payableUpgradePrice !== 0 && Math.round(payableUpgradePrice) === 0
    ? 'dynamicDollar'
    : 'roundedDollar'

  const formattedUpgradePrice = useMemo(() => formatCurrency(
    payableUpgradePrice,
    currency,
    currentRegionCode,
    { format: upgradePriceFormat, signDisplay: 'always' },
  ), [currency, currentRegionCode, payableUpgradePrice, upgradePriceFormat])

  const formattedUpgradeCtaPrice = useMemo(() => formatCurrency(
    payableUpgradePrice,
    currency,
    currentRegionCode,
    { format: upgradePriceFormat },
  ), [currency, currentRegionCode, payableUpgradePrice, upgradePriceFormat])

  const basePackageValue = useMemo(() => {
    if (charterBundleRoute && flightPrice) {
      return addFlightPrice(basePackageView.pricing, flightPrice).value
    }

    return basePackageView.pricing.value || basePackageView.package.value
  }, [basePackageView.package.value, basePackageView.pricing, charterBundleRoute, flightPrice])

  const upgradePackageValue = useMemo(() => {
    if (charterBundleRoute && flightPrice) {
      return addFlightPrice(upgradePackageView.pricing, flightPrice).value
    }

    return upgradePackageView.pricing.value || upgradePackageView.package.value
  }, [charterBundleRoute, flightPrice, upgradePackageView.package.value, upgradePackageView.pricing])

  const upgradeAddedValue = Math.max(0, upgradePackageValue - basePackageValue)
  const upgradeCost = payableUpgradePrice
  const upgradeValueSaving = upgradeAddedValue > 0
    ? (upgradeAddedValue - upgradeCost) / upgradeAddedValue
    : 0

  const formattedUpgradeAddedValue = useMemo(() => upgradeValueSaving >= MIN_UPGRADE_VALUE_SAVING ? formatCurrency(
    upgradeAddedValue,
    currency,
    currentRegionCode,
  ) : undefined, [currency, currentRegionCode, upgradeAddedValue, upgradeValueSaving])

  const title = isSameRoomType
    ? intl.formatMessage(
        {
          id: 'SZK_15TyyPVwdSWjeFLs',
          defaultMessage: 'Upgrade your package for only {formattedUpgradePrice} extra',
          description: 'Title for the package upgrade modal when room type stays the same',
        },
        { formattedUpgradePrice },
      )
    : intl.formatMessage(
        {
          id: 'JLqxihvA0JoOch-6c3tQ',
          defaultMessage: 'Upgrade your room for only {formattedUpgradePrice} extra',
          description: 'Title for the room upgrade modal when room type changes',
        },
        { formattedUpgradePrice },
      )
  const subtitle = ''
  const isDismissible = isSpoofed

  const showModal = useContext(ModalContext)
  const loyaltyInfoIconClick = useCallback(() => {
    showModal(<LoyaltyGenericInfoModal />)
  }, [showModal])

  const upgradeButtonLabel = intl.formatMessage({
    id: 'reXmM_aWYsPC1FfLUA1J',
    defaultMessage: 'Upgrade',
    description: 'Action button label to confirm the room/package upgrade',
  })
  const noThanksButtonLabel = intl.formatMessage({
    id: 'BaWPkwQUGZMKXELabsQ3',
    defaultMessage: 'No thanks',
    description: 'Action button label to decline the room/package upgrade',
  })

  const loyaltyPointsContent = <>
    {anyPointsEarnFetching && <PriceRowLoyaltyPointsLoadingSkeleton tier={displayTier} size="S" />}
    {!anyPointsEarnError && !anyPointsEarnFetching && upgradeAdditionalPointsEarn > 0 && <PriceRowLoyaltyPoints
      tier={displayTier}
      earnablePoints={upgradeAdditionalPointsEarn}
      statusCredits={shouldHideStatusCreditsAndNights ? undefined : upgradeAdditionalStatusCreditsEarn}
      size="S"
      calculationType="additional"
      onInfoIconClick={loyaltyInfoIconClick}
    />}
  </>
  let luxPlusInclusionsContent: React.ReactNode
  if (showLuxPlusInclusions) {
    luxPlusInclusionsContent = <LuxPlusInclusions
      luxPlusInclusions={luxPlusInclusions}
      accommodationOffer={offer}
    />
  }

  const stayUpgradeModalDisplayMode = isMobile && isStayUpgradeModalMobileVariant ? 'mobile' :
    isTabletOrDesktop && isStayUpgradeModalDesktopVariant ? 'desktop' : undefined

  if (stayUpgradeModalDisplayMode) {
    return <StayUpgradeModalVariant
      offer={offer}
      basePackage={basePackageView.package}
      upgradePackage={upgradePackageView.package}
      displayMode={stayUpgradeModalDisplayMode}
      formattedUpgradePrice={formattedUpgradeCtaPrice}
      formattedUpgradeAddedValue={formattedUpgradeAddedValue}
      isUltraLux={!!offer.property?.isUltraLux}
      isDismissible={isDismissible}
      checkIn={checkIn}
      charterBundleRoute={charterBundleRoute}
      hasFeederFlightIncluded={isFeederIncluded}
      propertyTimezoneOffset={offer.property?.timezoneOffset}
      loyaltyPointsContent={isLoyaltyEnabled ? loyaltyPointsContent : undefined}
      luxPlusInclusionsContent={luxPlusInclusionsContent}
      onUpgradeClick={onUpgradeClick}
      onNoThanksClick={onNoThanksClick}
    />
  }

  return (
    <ModalBase size="L" height="full" tabletHeight="auto" dismissible={isDismissible}>
      <ModalHeader
        dismissible={isDismissible}
        title={title}
        subtitle={subtitle}
      />
      <ModalBody>
        <Breakpoint max="mobile">
          <div>
            {!!roomType?.images.length && <ImageGallery
              images={roomType?.images}
              imageParams={imageProps}
            />}
          </div>
        </Breakpoint>
        <ModalContent>
          <Group direction="vertical" gap={12}>
            {isSpoofed && !isStoreMode && <PackageUpgradeSurchargeMarginComparisonText basePackage={basePackageView.package} upgradePackage={upgradePackageView.package} checkIn={checkIn} />}
            <VerticalSpacer gap={12}>
              <CSSBreakpoint min="tablet">
                <VerticalSpacer gap={12}>
                  <Group direction="horizontal" horizontalAlign="space-between" verticalAlign="end">
                    <VerticalSpacer gap={12}>
                      <Heading variant="heading4">{name}</Heading>
                      {roomType?.sizeSqm && <BodyText variant="small">
                        <FormattedMessage
                          id="vrraA51gEKK4hbndMBfD"
                          defaultMessage="<b>Room size:</b> {sizeSqm}m²"
                          description="Room size with label in the upgrade modal (tablet)"
                          values={{
                            sizeSqm: roomType.sizeSqm,
                            b: (chunks: React.ReactNode) => <b>{chunks}</b>,
                          }}
                        />
                      </BodyText>}
                      <LePackageUpgradePrice packageUpgrade={packageUpgrade} hasFlightsSelected={hasFlights} />
                      {isCurrentRegionUserLoyaltyRegion && loyaltyPointsContent}
                    </VerticalSpacer>
                    {firstRoomImageId && <ResponsiveImage
                      {...imageProps}
                      id={firstRoomImageId}
                      alt={roomImageAlt}
                    />}
                  </Group>
                </VerticalSpacer>
              </CSSBreakpoint>
              <CSSBreakpoint max="mobile">
                <VerticalSpacer gap={12}>
                  <Heading variant="heading4">{name}</Heading>
                  <LePackageUpgradePrice packageUpgrade={packageUpgrade} hasFlightsSelected={hasFlights} />
                  {isLoyaltyEnabled && loyaltyPointsContent}
                  {roomType?.sizeSqm && <BodyText variant="small">
                    <FormattedMessage
                      id="vrraA51gEKK4hbndMBfD"
                      defaultMessage="<b>Room size:</b> {sizeSqm}m²"
                      description="Room size with label in the upgrade modal (tablet)"
                      values={{
                        sizeSqm: roomType.sizeSqm,
                        b: (chunks: React.ReactNode) => <b>{chunks}</b>,
                      }}
                    />
                  </BodyText>}
                </VerticalSpacer>
              </CSSBreakpoint>
            </VerticalSpacer>
            <CSSBreakpoint min="tablet">
              <Divider kind="primary" />
            </CSSBreakpoint>

            <VerticalSpacer gap={16}>
              {luxPlusInclusionsContent}
              {(!!inclusions.length || !!bonusInclusions.length) && <Group direction="vertical" gap={12}>
                <BodyText variant="small" weight="bold">
                  {intl.formatMessage(
                    {
                      id: '62_B_n4yniSc_4dS28I8',
                      defaultMessage: '{count, plural, one {# inclusion} other {# inclusions}}:',
                      description: 'Inclusions count label in the package upgrade modal',
                    },
                    { count: inclusions.length + bonusInclusions.length },
                  )}
                </BodyText>
                <InclusionsSection offer={offer} inclusions={inclusions} bonusInclusions={bonusInclusions} size="small" />
              </Group>}
              {roomType && <PackageUpgradeRoomDetailsExpander roomType={roomType} />}
            </VerticalSpacer>
          </Group>
        </ModalContent>
      </ModalBody>
      <ModalFooter
        primaryActionProps={{
          onClick: onUpgradeClick,
          children: upgradeButtonLabel,
        }}
        secondaryActionProps={{
          onClick: onNoThanksClick,
          children: noThanksButtonLabel,
        }}
      />
    </ModalBase>
  )
}

function mapStateToProps(state: App.State): MappedProps {
  return {
    isSpoofed: state.auth.account.isSpoofed,
    isStoreMode: state.system.storeMode,
    isLoyaltyEnabled: getIsLoyaltyEnabled(state),
  }
}

export default connect(mapStateToProps)(PackageUpgradeModal)
